import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import AuthShell from '../Auth/AuthShell';
import { isParticipantDomain } from '../../utils/participantDomain';

// FECOA-1998: keep password managers out of the participant sign-in fields.
// Participant number and PIN are not browser credentials, but the markup shape
// (a text input followed by a type=password input inside a form) is what
// Chrome's built-in manager classifies as a sign-in form, and it ignores
// autoComplete="off" once it has. The PIN inputs therefore declare
// "new-password" — the pattern already proven on CaregiverFirstLoginPin — and
// these attributes opt the third-party managers out of every field.
const noPwManager = {
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
};

function ParticipantLogin() {
    const apiUrl = import.meta.env.VITE_API_URL;
    const [participantNumber, setParticipantNumber] = useState('');
    const [studyCode, setStudyCode] = useState('');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [isFirstLogin, setIsFirstLogin] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { participantLogin } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Extract study code from URL hash
    useEffect(() => {
        const extractStudyCodeFromHash = () => {
            const hash = window.location.hash;
            if (hash && hash.startsWith('#')) {
                const studyCodeFromHash = hash.substring(1); // Remove the # symbol
                console.log('Extracted study code from hash:', studyCodeFromHash);
                setStudyCode(studyCodeFromHash);
            } else {
                console.warn('No study code found in URL hash');
                setError('No study code found in URL. Please use the correct participant link.');
            }
        };

        extractStudyCodeFromHash();
        
        // Listen for hash changes in case the URL changes
        const handleHashChange = () => {
            extractStudyCodeFromHash();
        };
        
        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    const handleParticipantNumberChange = (event) => {
        // Only allow numeric input
        const value = event.target.value.replace(/\D/g, '');
        setParticipantNumber(value);
    };

    const handlePinChange = (event) => {
        setPin(event.target.value);
    };

    const handleConfirmPinChange = (event) => {
        setConfirmPin(event.target.value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Check if this is a first login
            const response = await axios.post(`${apiUrl}/api/participant/first_login`, {
                study_code: studyCode,
                participant_number: participantNumber
            });

            // Updated to handle the new response format
            if (response.data.is_first_login === true) {
                setIsFirstLogin(true);
            } else {
                setIsFirstLogin(false);
            }
        } catch (error) {
            console.error('First login check error:', error);
            setError('Error occurred while checking participant number. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFirstLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        if (pin !== confirmPin) {
            setError('PINs do not match. Please try again.');
            setLoading(false);
            return;
        }

        try {
            // Set PIN with the new participant identification format
            const response = await axios.post(`${apiUrl}/api/participant/set_pin`, {
                study_code: studyCode,
                participant_number: participantNumber,
                pin
            });

            // The API now returns tokens directly after PIN setup
            if (response.data.access_token && response.data.refresh_token) {
                // Use the AuthContext to set user state and redirect
                const userData = {
                    tokens: {
                        access_token: response.data.access_token,
                        refresh_token: response.data.refresh_token
                    },
                    user: response.data.user || { id: response.data.participant_id },
                    user_type: 'participant'
                };
                
                // Use the context to login
                await participantLogin(participantNumber, pin, studyCode, false);
            } else {
                setError('Error occurred during PIN setup. Please try again.');
            }
        } catch (error) {
            console.error('PIN setup error:', error);
            // Display specific error message from backend if available
            const errorMessage = error.response?.data?.error || 
                                 error.response?.data?.message || 
                                 'Error occurred during PIN setup. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            // Use AuthContext's participantLogin method that we fixed
            await participantLogin(participantNumber, pin, studyCode, false);
            // The redirect is handled by the AuthContext
        } catch (error) {
            console.error('Login error:', error);
            setError('Invalid participant ID or PIN. Please check your credentials and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Participant pages are always light, so these classes have no dark variants.
    const fieldClass = 'block h-12 w-full rounded-lg border border-gray-300 bg-white px-3.5 text-base text-gray-900 shadow-xs placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15';
    const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700';
    const errorBox = error && (
        <p className="error rounded-lg border border-danger-500/25 bg-danger-50 px-3.5 py-3 text-sm text-danger-700" role="alert">
            {error}
        </p>
    );

    // Don't render the form if we don't have a study code yet
    if (!studyCode) {
        return (
            <div className="force-light-mode participant-login">
                <AuthShell
                    forceLight
                    title="Participant sign in"
                    subtitle={error ? undefined : 'Loading...'}
                >
                    {error ? (
                        <div className="space-y-5">
                            {errorBox}
                            <p className="text-sm leading-relaxed text-gray-500">
                                Open the sign-in link from your study team again. It includes your study&apos;s code.
                            </p>
                            {isParticipantDomain() && (
                                <a href="/" className="btn btn-primary btn-lg w-full">Go to sign in</a>
                            )}
                        </div>
                    ) : (
                        <Loader2 className="h-6 w-6 animate-spin text-primary-600" aria-hidden="true" />
                    )}
                </AuthShell>
            </div>
        );
    }

    return (
        <div className="force-light-mode participant-login">
            <AuthShell
                forceLight
                title={isFirstLogin === null ? 'Enter ID' : (isFirstLogin ? 'Create PIN' : 'Enter PIN')}
                subtitle={<span className="study-info">Study: <span className="font-medium text-gray-900">{studyCode}</span></span>}
            >
                <form
                    className="space-y-5"
                    onSubmit={isFirstLogin === null ? handleSubmit : (isFirstLogin ? handleFirstLogin : handleLogin)}
                >
                    <div className="input-group">
                        <label htmlFor="participant-reference" className={labelClass}>Participant Number</label>
                        <input
                            type="text"
                            id="participant-reference"
                            name="participant-reference"
                            autoComplete="off"
                            value={participantNumber}
                            onChange={handleParticipantNumberChange}
                            inputMode="numeric"
                            pattern="\d*"
                            required
                            className={`participant-id-input ${fieldClass}`}
                            {...noPwManager}
                        />
                    </div>
                    {isFirstLogin === true && (
                        <>
                            <div className="input-group">
                                <label htmlFor="participant-access-code" className={labelClass}>Create PIN</label>
                                <input
                                    type="password"
                                    id="participant-access-code"
                                    name="participant-access-code"
                                    autoComplete="new-password"
                                    value={pin}
                                    onChange={handlePinChange}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    required
                                    className={fieldClass}
                                    {...noPwManager}
                                />
                            </div>
                            <div className="input-group">
                                <label htmlFor="participant-access-code-confirm" className={labelClass}>Confirm PIN</label>
                                <input
                                    type="password"
                                    id="participant-access-code-confirm"
                                    name="participant-access-code-confirm"
                                    autoComplete="new-password"
                                    value={confirmPin}
                                    onChange={handleConfirmPinChange}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    required
                                    className={fieldClass}
                                    {...noPwManager}
                                />
                            </div>
                        </>
                    )}
                    {isFirstLogin === false && (
                        <div className="input-group">
                            <label htmlFor="participant-access-code" className={labelClass}>Enter PIN</label>
                            <input
                                type="password"
                                id="participant-access-code"
                                name="participant-access-code"
                                autoComplete="new-password"
                                value={pin}
                                onChange={handlePinChange}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                                className={fieldClass}
                                {...noPwManager}
                            />
                        </div>
                    )}
                    {errorBox}
                    <button type="submit" className="btn btn-primary btn-lg w-full" disabled={loading}>
                        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                        {isFirstLogin === null ? 'Next' : 'Login'}
                    </button>
                </form>
            </AuthShell>
        </div>
    );
}

export default ParticipantLogin;
